/**
 * PaybillModal — manual M-Pesa Paybill payment submission.
 *
 * Users see:
 *   1. The Paybill number, Account number, and Amount to send
 *   2. Copy-to-clipboard buttons for each field
 *   3. An M-Pesa reference input + "I have paid" submit button
 *
 * On submit, POSTs to /paybill/submit which records the row as
 * `pending_super_admin_verification` and waits for admin approval.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Copy, CheckCircle2, Loader2, Info } from "lucide-react";

export default function PaybillModal({ open, planCode, onClose, onSubmitted }) {
    const [config, setConfig] = useState(null);
    const [mpesaRef, setMpesaRef] = useState("");
    const [phone, setPhone] = useState("");
    const [copied, setCopied] = useState(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    useEffect(() => {
        if (!open) return;
        api.get("/paybill/config").then(({ data }) => setConfig(data)).catch(() => {});
        setDone(null); setMpesaRef(""); setPhone("");
    }, [open]);

    if (!open) return null;
    if (!config) {
        return createPortal(<Backdrop onClose={onClose}><div className="text-white">Loading…</div></Backdrop>, document.body);
    }
    const plan = config.plans?.[planCode];
    if (!plan) {
        return createPortal(<Backdrop onClose={onClose}>
            <div className="text-white p-6 text-center max-w-sm">Unknown plan: {planCode}</div>
        </Backdrop>, document.body);
    }

    async function copy(text, key) {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(key);
            setTimeout(() => setCopied(null), 1500);
        } catch { toast.error("Copy failed"); }
    }

    async function submit() {
        if (mpesaRef.trim().length < 6) {
            toast.error("Enter your M-Pesa confirmation code (e.g. SFN0X8A7BC)");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/paybill/submit", {
                plan_code: planCode,
                mpesa_ref: mpesaRef.trim().toUpperCase(),
                phone_used: phone.trim() || undefined,
            });
            setDone(data.submission);
            onSubmitted?.(data.submission);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit — please try again.");
        } finally { setBusy(false); }
    }

    return createPortal(
        <Backdrop onClose={onClose}>
            <div
                className="w-full max-w-lg bg-card border-2 border-border rounded-2xl overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="paybill-modal"
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                    <div>
                        <div className="overline text-primary tracking-widest">M-Pesa Paybill Payment</div>
                        <div className="font-bold text-lg">{planLabel(planCode)}</div>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-border/60 rounded" data-testid="paybill-close-btn">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {done ? (
                    <SuccessBody sub={done} onClose={onClose} />
                ) : (
                    <>
                        <div className="p-5 space-y-3">
                            <PayField label="Paybill" value={config.paybill_number}
                                copied={copied === "pb"} onCopy={() => copy(config.paybill_number, "pb")} testId="paybill-num" />
                            <PayField label="Account #" value={config.account_number}
                                copied={copied === "acc"} onCopy={() => copy(config.account_number, "acc")} testId="paybill-acc" />
                            <PayField label="Amount"
                                value={`KES ${plan.amount_kes.toLocaleString()}`}
                                copied={copied === "amt"} onCopy={() => copy(String(plan.amount_kes), "amt")}
                                testId="paybill-amt" />
                            <div className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-1">
                                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                <span>
                                    Payments to <b>{config.account_name}</b>. After sending, paste the
                                    M-Pesa confirmation code below and tap "I have paid". Your subscription
                                    activates the next midnight (UTC) once Super Admin verifies.
                                </span>
                            </div>
                        </div>
                        <div className="p-5 border-t border-border space-y-3">
                            <label className="block">
                                <span className="text-xs text-muted-foreground">M-Pesa confirmation code</span>
                                <Input
                                    value={mpesaRef}
                                    onChange={(e) => setMpesaRef(e.target.value.toUpperCase())}
                                    placeholder="e.g. SFN0X8A7BC"
                                    className="mono uppercase mt-1"
                                    autoCapitalize="characters"
                                    data-testid="paybill-mpesa-ref"
                                    maxLength={16}
                                />
                            </label>
                            <label className="block">
                                <span className="text-xs text-muted-foreground">Phone used (optional)</span>
                                <Input
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="+2547XXXXXXXX"
                                    className="mono mt-1"
                                    data-testid="paybill-phone"
                                />
                            </label>
                            <Button
                                className="w-full h-11"
                                disabled={busy || mpesaRef.trim().length < 6}
                                onClick={submit}
                                data-testid="paybill-submit-btn"
                            >
                                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                I have paid
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </Backdrop>,
        document.body,
    );
}

function planLabel(code) {
    return {
        advanced_monthly: "Advanced Trader · KES 1,000 / 30 days",
        moe_monthly:      "MoE Institutional · KES 35,000 / 30 days",
        moe_annual:       "MoE Institutional · KES 350,000 / 365 days",
        cma_monthly:      "CMA Institutional · KES 50,000 / 30 days",
        cma_annual:       "CMA Institutional · KES 500,000 / 365 days",
    }[code] || code;
}

function Backdrop({ children, onClose }) {
    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="paybill-backdrop"
        >
            {children}
        </div>
    );
}

function PayField({ label, value, copied, onCopy, testId }) {
    return (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-background border border-border">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className="mono font-bold text-lg" data-testid={testId}>{value}</div>
            </div>
            <button
                onClick={onCopy}
                className="h-8 w-8 flex items-center justify-center rounded border border-border hover:border-primary transition-colors"
                data-testid={`${testId}-copy-btn`}
            >
                {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
            </button>
        </div>
    );
}

function SuccessBody({ sub, onClose }) {
    return (
        <div className="p-6 text-center space-y-4" data-testid="paybill-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 border-2 border-emerald-500/40 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <div>
                <div className="text-xl font-black">Submitted for verification</div>
                <p className="text-sm text-muted-foreground mt-2">
                    Reference <b className="mono">{sub.mpesa_ref}</b>. Super Admin will
                    verify within 24 hours. You'll get an in-app notification once your
                    subscription is active.
                </p>
            </div>
            <Button variant="outline" onClick={onClose} data-testid="paybill-close-success">Close</Button>
        </div>
    );
}
